import type React from "react";
import { useMemo } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Button, DotsLoader, SearchInput } from "@similarweb/ui-components";
import { Text } from "@similarweb/ui-components/foundations";

import { ContactCards } from "app/pages/sales-intelligence/pages/ai-agent/containers/Attendees/ContactCards";
import { useAttendeesValue } from "app/pages/sales-intelligence/pages/ai-agent/containers/Attendees/useAttendeesValue";
import { MAX_DISPLAYED_CONTACTS } from "app/pages/sales-intelligence/pages/ai-agent/containers/Attendees/utils";
import { translate } from "app/filters/i18nFilter";
import type { Contact } from "app/pages/sales-intelligence/modules/ai-agent/types/meeting";
import { StyledSearchInputContainer } from "app/pages/sales-intelligence/pages/ai-agent/containers/Attendees/styles";

type Props = {
    value: Contact[];
    onChange: (value: Contact[]) => void;
    disabled: boolean;
};

export const Attendees: React.FC<Props> = ({ value, onChange, disabled }) => {
    const {
        filteredValue,
        errorType,
        shouldClearSearch,
        isButtonVisible,
        isLoading,
        handleAddNewContact,
        handleCheckboxClick,
        handleSearchChange,
        handleSearchFocus,
    } = useAttendeesValue({ value, onChange });

    const displayedContacts = useMemo(() => filteredValue.slice(0, MAX_DISPLAYED_CONTACTS), [filteredValue]);

    return (
        <div>
            {disabled ? null : (
                <StyledSearchInputContainer
                    isError={!!errorType}
                    isLoading={isLoading}
                >
                    <SearchInput
                        debounce={300}
                        onChange={handleSearchChange}
                        placeholder={translate("si.pages.ai_agent.form.keyAttendees.search.placeholder")}
                        disableClear={true}
                        clearValue={shouldClearSearch}
                        onFocus={handleSearchFocus}
                        isLoading={isLoading}
                        loadingComponent={<DotsLoader className="SearchInput-dots-loader" />}
                    />
                    {errorType ? (
                        <Text
                            fontVariant="caption"
                            color={colorsPalettes.red[400]}
                        >
                            {translate("si.pages.ai_agent.form.keyAttendees.search.error", { errorType })}
                        </Text>
                    ) : null}
                </StyledSearchInputContainer>
            )}
            <ContactCards
                onSelect={handleCheckboxClick}
                value={displayedContacts}
                isDisabled={disabled}
                isLoading={isLoading}
            />
            {isButtonVisible ? (
                <Button
                    type="flat"
                    onClick={handleAddNewContact}
                    isDisabled={isLoading}
                >
                    {translate("si.pages.ai_agent.form.keyAttendees.button.add")}
                </Button>
            ) : null}
        </div>
    );
};
